import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface RecordStat {
  readonly label: string;
  readonly value: ReactNode;
  readonly note?: ReactNode;
}

interface RecordHeaderProps {
  /** Names the region. The title is the record's own name, which is not that. */
  readonly label: string;
  readonly title: string;
  /** Two letters in a circle. Omitted for a record that is not a person. */
  readonly initials?: string;
  /** Badges beside the title — entitlement, status, kind. */
  readonly badges?: ReactNode;
  /**
   * The identifying facts, rendered as one line separated by slashes. Nulls are
   * dropped, so a record missing a timezone does not show a stray separator.
   */
  readonly meta?: readonly (string | null | undefined)[];
  readonly actions?: ReactNode;
  /** The bordered strip under the identity row. Omitted when there is nothing to count. */
  readonly stats?: readonly RecordStat[];
  /**
   * A closing line under the identity row — the standing caveat about the
   * record as a whole. The design gives the food record one of these for what
   * already points at the row; it is the right home for anything an operator
   * has to know before touching anything below.
   */
  readonly note?: ReactNode;
  /** The record's tabs, as the card's last row: Details and History. */
  readonly tabs?: ReactNode;
}

const STAT_COLUMNS: Readonly<Record<number, string>> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-5',
};

/**
 * The card every record screen opens with.
 *
 * One shape for users, foods and exercises, because in the design they are one
 * shape: identity and actions on top, then a divided strip of the numbers that
 * describe the record. Three screens hand-rolling it is three chances for the
 * avatar to be a different size on each.
 *
 * The meta line is slash-separated rather than stacked: these are identifiers,
 * read left to right, and stacking them would give four short lines the same
 * vertical weight as the name above them.
 */
export function RecordHeader({
  label,
  title,
  initials,
  badges,
  meta = [],
  actions,
  stats = [],
  note,
  tabs,
}: RecordHeaderProps) {
  const shown = meta.filter((item): item is string => item !== null && item !== undefined && item !== '');

  return (
    <section
      aria-label={label}
      className="flex flex-col overflow-hidden rounded-xl border border-border bg-card"
    >
      <div className="flex flex-wrap items-start gap-3.5 px-[18px] pt-[17px] pb-[15px]">
        {initials === undefined ? null : (
          <span
            aria-hidden="true"
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/12 text-[13px] font-semibold text-primary"
          >
            {initials}
          </span>
        )}

        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[17px] font-semibold tracking-[-0.015em] text-foreground">{title}</h2>
            {badges}
          </div>
          {shown.length === 0 ? null : (
            <div className="flex flex-wrap items-center gap-2.5 text-[13px] text-muted-foreground">
              {shown.map((item, index) => (
                <span key={item} className="flex items-center gap-2.5">
                  {index === 0 ? null : (
                    <span aria-hidden="true" className="text-input">
                      /
                    </span>
                  )}
                  <span className={index === 0 ? 'font-mono' : undefined}>{item}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {actions === undefined ? null : (
          <div className="ml-auto flex shrink-0 flex-wrap items-center gap-[7px]">{actions}</div>
        )}
      </div>

      {note === undefined ? null : (
        <div className="flex items-center gap-3.5 border-t border-border px-[18px] py-3">{note}</div>
      )}

      {tabs === undefined ? null : <div className="flex gap-1 border-t border-border px-3">{tabs}</div>}

      {stats.length === 0 ? null : (
        <div
          className={cn(
            'grid border-t border-border [&>*:last-child]:border-r-0',
            STAT_COLUMNS[stats.length] ?? 'grid-cols-2 sm:grid-cols-4',
          )}
        >
          {stats.map((stat) => (
            <div key={stat.label} className="flex flex-col gap-0.5 border-r border-border px-[18px] py-3">
              <span className="text-xs text-muted-foreground">{stat.label}</span>
              {/* The inline figure, the same one `StatStrip` draws. */}
              <span className="font-mono text-base font-medium tabular-nums text-foreground">
                {stat.value}
              </span>
              {stat.note === undefined ? null : (
                <span className="text-xs text-muted-foreground">{stat.note}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
